import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import OTPInput from '../components/OTPInput';
import { ArrowLeft, Loader2 } from 'lucide-react';
import Button from '../components/ui/Button';

export default function VerifyOTP() {
  const [email, setEmail] = useState<string | null>(sessionStorage.getItem('reset_email'));
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Hash the OTP code
      const encoder = new TextEncoder();
      const data = encoder.encode(otp);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashedOtp = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      // Call verify-otp function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/verify_otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          p_email: email,
          p_code: hashedOtp
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to verify OTP');
      }

      if (result.verified) {
        // Store verification status
        sessionStorage.setItem('otp_verified', 'true');
        navigate('/reset-password', { state: { email, otp: hashedOtp } });
      } else {
        setError(result.error || 'Invalid verification code. Please try again.');
      }
    } catch (err) {
      console.error('Error verifying OTP:', err);
      setError(err instanceof Error ? err.message : 'Failed to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPComplete = (code: string) => {
    setOtp(code);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <button
            onClick={() => navigate('/forgot-password')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Forgot Password
          </button>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify Your Email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter the verification code sent to {email}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleVerifyOTP}>
          <div className="space-y-4">
            <OTPInput length={6} onComplete={handleOTPComplete} />
            
            {error && (
              <div className="text-sm text-red-600 text-center bg-red-50 border border-red-100 rounded-lg p-3">
                {error}
              </div>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading || otp.length !== 6}
            className="w-full flex justify-center items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              Didn't receive a code? Try again
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}